import { Typography } from '@mui/material';
import { Link, useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useRecoilValue } from 'recoil';
import { ActionsCell } from '../../../Component/Common/Components/DataTable/ActionsCell';
import { DefaultCell } from '../../../Component/Common/Components/DataTable/DefaultCell';
import {
    BEELockScorecards,
    ClientRole,
    SiteAdminRole,
    SubAdminRole,
    UserAdminRole
} from '../../../Component/Common/Constants/UserRoles';
import { ScorecardLicenseSummaryDataCore } from '../../../Component/Model/Dto/ScorecardLicense';
import { ActionType, ILinkProps, TableColumn } from '../../../Component/Model/Table/Table';
import { DateFormatter } from '../../../Component/Model/helpers';
import { getLinkTo } from '../../../Helpers/linkHelper';
import { isExpired } from '../../../Helpers/scorecardHelper';
import { userRolesState } from '../../../State/user.Atom';
import config from '../../../config';
import { decimalFormatter } from '../../../Helpers/DecimalFormatter';

interface ScorecardRows {
    id: string;
    name: ILinkProps;
    scorecardType: string;
    createdDate: string;
    isConsolidated: string;
    fromDate: string;
    toDate: string;
    totalPoints: string | number;
    ratingLevel: string;
    adjustedLevel: string;
    actions: ActionType[];
}

export interface ScorecardTable {
    columns: Array<TableColumn>;
    rows: Array<ScorecardRows>;
}

export function ScorecardLicenseTableMapper(results: ScorecardLicenseSummaryDataCore[], handleAssignUser: (id: string) => void, handleLockScorecard: (id: string) => void, deleteScorecard: (id: string) => void, handleGoToVerificationPlanner: (id: string, name: string, scorecardTypeId: number) => void, isSharedForVerification? : boolean): ScorecardTable {
    const history = useHistory();
    const userRoles = useRecoilValue(userRolesState);
    const showRouteToPlanner =  config.enable_VAIntegrationSubmissionProcess === 'true';
    const useClientBehaviour = (userRoles.includes(ClientRole) || isSharedForVerification);
    const useIframePages = config.enable_iframe_pages === 'true';

    function getActionColumns(item: ScorecardLicenseSummaryDataCore, index: number): ActionType[] {
        const actions: ActionType[] = [];
        
        if (useClientBehaviour && showRouteToPlanner) {
            actions.push(
            {
                key: index.toString(),
                label: 'Open Verification Planner & Tracker',
                action: () => {
                    handleGoToVerificationPlanner(item.scorecardId, item.scorecardName, item.scorecardTypeId);
                },
                icon: {
                    color: 'secondary',
                    name: 'assignment'
                }
            });
        }
        
        if (userRoles.includes(UserAdminRole) || userRoles.includes(SubAdminRole) || userRoles.includes(BEELockScorecards)) {
            if (item.isAllLocked || item.isLocked) {
                actions.push({
                    key: index.toString(),
                    label: 'Locked',
                    action: () => {
                        if (item.isLocked) {
                            toast.info("This scorecard is locked by the System Admin cannot be unlocked.");
                        } else {
                            handleLockScorecard(item.scorecardId);
                        }
                    },
                    icon: {
                        color: 'primary',
                        name: 'lock'
                    }
                });
            }
            else {
                actions.push({
                    key: index.toString(),
                    label: 'Unlocked',
                    action: () => {
                        handleLockScorecard(item.scorecardId);
                    },
                    icon: {
                        color: 'primary',
                        name: 'lock_open'
                    }
                });
            }
        }

        if (!isSharedForVerification && (userRoles.includes(SiteAdminRole) || userRoles.includes(UserAdminRole) || userRoles.includes(SubAdminRole))) {
            actions.push(
                {
                    key: index.toString(),
                    label: 'Assign Users',
                    action: () => {
                        handleAssignUser(item.scorecardId);
                    },
                    icon: {
                        color: 'primary',
                        name: 'person_add'
                    }
                },
            )
        }

        return actions;
    }

    const columns = [
        { Header: 'Name', accessor: 'name', id: "scorecardName", Cell: ({ value }: any) => {
            if (value.isExpired) {
                return (
                    <Typography
                        variant="caption"
                        fontWeight='regular'
                        color="#007bff"
                        style={{ cursor: "pointer" }}
                        onClick={() => toast.error('The scorecard license has expired. Please renew your license before you can edit')}
                    >
                        {value.itemName}
                    </Typography>
                );
            }
            if (!useIframePages) {
                return (
                    <Typography
                        variant="caption"
                        fontWeight='regular'
                        color="#007bff"
                        component="a"
                        href={value.linkTo}
                        style={{ textDecoration: 'none' }}
                    >
                        {value.itemName}
                    </Typography>
                );
            }
            return (
                <Typography variant="caption" fontWeight='regular' color="#007bff" component={Link} to={{ pathname: value.linkTo, state: value.linkState }}>
                    {value.itemName}
                </Typography>
            );
        }},
        { Header: 'Scorecard Type', accessor: 'scorecardType', Cell: ({ value }: any) => <DefaultCell value={value} /> },
        { Header: 'Created Date', accessor: 'createdDate', Cell: ({ value }: any) => <DefaultCell value={value} /> },
        { Header: 'Consolidated Scorecard', accessor: 'isConsolidated', id: "consolidated", Cell: ({ value }: any) => <DefaultCell value={value} /> },
        { Header: 'From Date', accessor: 'fromDate', id: "startDate", Cell: ({ value }: any) => <DefaultCell value={value} /> },
        { Header: 'To Date', accessor: 'toDate', id: "endDate", Cell: ({ value }: any) => <DefaultCell value={value} /> },
        { Header: (useClientBehaviour ? 'Verified Total Points':'Total Points'), accessor: 'totalPoints', id: "totalPoints", Cell: ({ value }: any) => <DefaultCell value={value} /> },
        { Header: (useClientBehaviour ? 'Verified Rating Level':'Rating Level'), accessor: 'ratingLevel', id: "ratingLevel", Cell: ({ value }: any) => <DefaultCell value={value} /> },
        { Header: (useClientBehaviour ? 'Verified Adjusted Level':'Adjusted Level'), accessor: 'adjustedLevel', id: "adjustedLevel", Cell: ({ value }: any) => <DefaultCell value={value} /> },
        { Header: 'Actions', accessor: 'actions', Cell: ({ value }: any) => <ActionsCell actions={value} />, disableSortBy: true, }
    ];
    if (results.length > 0) {
        const rows = results.map((item: ScorecardLicenseSummaryDataCore, index: number) => ({
            id: item.scorecardId,
            name: {
                linkTo: useIframePages
                    ? `/bee/scorecard/${item.scorecardId}/summary/dashboard`
                    : useClientBehaviour
                        ? `${config.publicUrl}/#general/${item.scorecardId}`
                        : `${config.publicUrl}/#scorecard/${item.scorecardId}/dashboard`,
                linkState: { scorecardName: item.scorecardName, scorecardTypeId: item.scorecardTypeId, isConsolidated: item.isConsolidated },
                itemName: item.scorecardName,
                isExpired: isExpired(item.expiryDate)
            },
            scorecardType: item.scorecardType,
            createdDate: DateFormatter(item.createdDate.toString()),
            isConsolidated: item.isConsolidated ? 'Yes' : 'No',
            fromDate: DateFormatter(item.startDate.toString()),
            toDate: DateFormatter(item.endDate.toString()),
            totalPoints: decimalFormatter(item.totalPoints, 2),
            ratingLevel: item.ratingLevel,
            adjustedLevel: item.adjustedLevel,
            actions: getActionColumns(item, index)
        }));
        return { columns: columns, rows: rows };
    }
    else {
        return { columns: columns, rows: [] };
    }
}


